<template>
  <div
    class="vertical-layout h-100"
    :class="[layoutClasses]"
    :data-col="isNavMenuHidden ? '1-column' : null"
  >
    <div v-show="!$store.state.app.isAdmin" class="whatsapp-button" @mousedown="startDrag">
      <div class="whatsapp-button__wrapper">
        <img :src="chatIcon" alt="chat_icon">
        <img :src="chatIconMessage" alt="chat_icon" @click="onChatIconClick">
      </div>
    </div>
    <!-- 08vbar -->
    <b-navbar
      :toggleable="false"
      :variant="navbarBackgroundColor"
      class="header-navbar navbar navbar-shadow align-items-center"
      :class="[navbarTypeClass,{ 'navbar--hidden': !showNavbar }]"
    >
      <slot
        name="navbar"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :navbarBackgroundColor="navbarBackgroundColor"
        :navbarTypeClass="[...navbarTypeClass, 'header-navbar navbar navbar-shadow align-items-center']"
      >
        <app-navbar-vertical-layout :toggle-vertical-menu-active="toggleVerticalMenuActive" />
      </slot>
    </b-navbar>
    <!--/ Navbar -->

    <!-- Vertical Nav Menu -->
    <vertical-nav-menu
      v-if="!isNavMenuHidden"
      :is-vertical-menu-active="isVerticalMenuActive"
      :toggle-vertical-menu-active="toggleVerticalMenuActive"
    >
      <template #header="slotProps">
        <slot
          name="vertical-menu-header"
          v-bind="slotProps"
        />
      </template>
    </vertical-nav-menu>
    <!-- /Vertical Nav Menu -->

    <!-- Vertical Nav Menu Overlay -->
    <div
      class="sidenav-overlay"
      :class="overlayClasses"
      @click="isVerticalMenuActive = false"
    />
    <!-- /Vertical Nav Menu Overlay -->

    <!-- Content -->

    <!-- CONTENT TYPE: Left -->
    <transition
      :name="routerTransition"
      mode="out-in"
    >
      <component
        :is="layoutContentRenderer"
        :key="layoutContentRenderer === 'layout-content-renderer-left' ? $route.meta.navActiveLink || $route.name : null"
      >
        <template
          v-for="(index, name) in $scopedSlots"
          v-slot:[name]="data"
        >
          <slot
            :name="name"
            v-bind="data"
          />
        </template>
      </component>
    </transition>
    <!--/ Content -->

    <!-- Footer -->
    <footer
      class="footer footer-light"
      :class="[footerTypeClass]"
    >
      <slot name="footer">
        <app-footer />
      </slot>
    </footer>
    <!-- /Footer -->

    <slot name="customizer" />
  </div>
</template>

<script>
import { onUnmounted } from '@vue/composition-api'
import AppNavbarVerticalLayout from '@core/layouts/components/app-navbar/AppNavbarVerticalLayout.vue'
import AppFooter from '@core/layouts/components/AppFooter.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import { BNavbar } from 'bootstrap-vue'
import LayoutContentRendererDefault from '@core/layouts/components/layout-content-renderer/LayoutContentRendererDefault.vue'
import LayoutContentRendererLeft from '@core/layouts/components/layout-content-renderer/LayoutContentRendererLeft.vue'
import LayoutContentRendererLeftDetached from '@core/layouts/components/layout-content-renderer/LayoutContentRendererLeftDetached.vue'
import VerticalNavMenu from './components/vertical-nav-menu/VerticalNavMenu.vue'
import useVerticalLayout from './useVerticalLayout'
import mixinVerticalLayout from './mixinVerticalLayout'

export default {
  components: {
    // AppBreadcrumb,
    AppNavbarVerticalLayout,
    AppFooter,
    VerticalNavMenu,
    BNavbar,
    LayoutContentRendererLeftDetached,
    LayoutContentRendererLeft,
    LayoutContentRendererDefault,
  },
  mixins: [mixinVerticalLayout],
  computed: {
    layoutContentRenderer() {
      const rendererType = this.$route.meta.contentRenderer
      if (rendererType === 'sidebar-left') return 'layout-content-renderer-left'
      if (rendererType === 'sidebar-left-detached') return 'layout-content-renderer-left-detached'
      return 'layout-content-renderer-default'
    },
  },
  setup() {
    const {
      routerTransition, navbarBackgroundColor, navbarType, footerType, isNavMenuHidden,
    } = useAppConfig()

    const {
      isVerticalMenuActive,
      toggleVerticalMenuActive,
      isVerticalMenuCollapsed,
      layoutClasses,
      overlayClasses,
      resizeHandler,
      navbarTypeClass,
      footerTypeClass,
    } = useVerticalLayout(navbarType, footerType)

    // Resize handler
    resizeHandler()
    window.addEventListener('resize', resizeHandler)
    onUnmounted(() => {
      window.removeEventListener('resize', resizeHandler)
    })

    return {
      isVerticalMenuActive,
      toggleVerticalMenuActive,
      isVerticalMenuCollapsed,
      overlayClasses,
      layoutClasses,
      navbarTypeClass,
      footerTypeClass,

      // App Config
      routerTransition,
      navbarBackgroundColor,
      isNavMenuHidden,
    }
  },
  data () {
    return {
      showNavbar: true,
      lastScrollPosition: 0,
      chatIcon: require('@/assets/images/chat_icon2.png'),
      chatIconMessage: require('@/assets/images/chat_icon3.png'),
      chatData: {
        number: '',
        message: ''
      },
      dragging: false,
      clickPrevented: false,
      offsetX: 0,
      offsetY: 0
    }
  },
  mounted () {
    this.getWhatsapp()
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll () {
      const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop
      if (currentScrollPosition < 0) {
        return
      }
      if (Math.abs(currentScrollPosition - this.lastScrollPosition) < 60) {
        return
      }
      this.showNavbar = currentScrollPosition < this.lastScrollPosition
      this.lastScrollPosition = currentScrollPosition
    },
    startDrag(event) {
      this.dragging = true
      this.clickPrevented = false
      this.offsetX = event.clientX - event.target.getBoundingClientRect().left
      this.offsetY = event.clientY - event.target.getBoundingClientRect().top
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },
    onDrag(event) {
      if (this.dragging) {
        const button = this.$el.querySelector('.whatsapp-button')
        button.style.left = `${event.clientX - this.offsetX}px`
        button.style.top = `${event.clientY - this.offsetY}px`
      }
    },
    stopDrag() {
      this.dragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      setTimeout(() => {
        this.clicked = true
      }, 100)
    },
    onChatIconClick(event) {
      if (!this.dragging) {
        const number = this.chatData.number
        const message = this.chatData.message
        window.open(`https://wa.me/${number}?text=${message}`, '_blank')
      }else{
        event.preventDefault();
      }
    },
    async getWhatsapp() {
      return await this.$http.get(`/whatsapp`)
      .then(res => {
        const data = res.data.data ?? {}
        this.chatData.number = data.number
        this.chatData.message = data.message
      })
    },
  }

}
</script>

<style lang="scss">
@import "~@core/scss/base/themes/bordered-layout.scss";
// .whatsapp-button {
//   position: relative;
//   z-index: 999999;
//   bottom: 50px;
//   right: 50px;
//   &__wrapper {
//     width: 100px;
//     height: 100px;
//     img {
//       width: 100px;
//       height: auto;
//       cursor: pointer;
//       transition: all 300ms ease;
//       position: absolute;
//       filter: drop-shadow(5px 5px 5px #222);
      
//       &:first-of-type {
//         top: 0;
//         left: 0;
//       }

//       &:last-of-type {
//         top: 14px;
//         left: -14px;
//         width: 25px;  
//       }
  
//       &:active {
//         scale: 1;
//       }
//     }

//     &:hover img {
//       &:first-of-type {
//         scale: 1.1;
//       }
//       &:last-of-type {
//         filter: initial;
//         top: -14px;
//         left: -40px;
//         scale: 4;
//       }
//     }
//   }
// }
// </style>
<style>
.whatsapp-button {
  position: fixed;
  z-index: 999999;
  bottom: 0;
  left: 15px;
  cursor: grab;
  width: 30%; 
  height: 80px;
}

.whatsapp-button:active {
  cursor: grabbing;
}

.whatsapp-button__wrapper {
  width: 60px;
  height: 60px;
  position: relative;
}

.whatsapp-button__wrapper img {
  width: 100%;
  height: auto;
  cursor: pointer;
  transition: all 300ms ease;
  position: absolute;
  filter: drop-shadow(5px 5px 5px #222);
}

.whatsapp-button__wrapper img:first-of-type {
  top: 0;
  left: 0;
}

.whatsapp-button__wrapper img:last-of-type {
  top: 14px;
  left: -14px;
  width: 25px;
}

.whatsapp-button__wrapper img:active {
  scale: 1;
}

.whatsapp-button__wrapper:hover img:first-of-type {
  scale: 1.1;
}

.whatsapp-button__wrapper:hover img:last-of-type {
  filter: initial;
  top: -14px;
  left: -40px;
  scale: 4;
}
.navbar--hidden {
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s, opacity 0.5s linear;
}
</style>
